export const WHATS_NEW_VERSION = "2026-06-03";

export const whatsNewUpdates = [
  {
    id: "background-effects",
    title: "Animated Background Effects",
    description: "Bring surveys to life with aurora, mesh gradient, film grain, and floating particle backdrops.",
    date: "New",
    detailedContent: {
      description: "Add ambient motion to any survey with a single click. Choose from aurora (northern-lights glow), mesh gradients, film grain, or floating particles. Effects adapt to your theme colors and automatically respect each respondent's reduced-motion preference.",
      benefits: [
        "Aurora, mesh gradient, film grain, and particle effects",
        "Automatically tinted to match your accent color",
        "Lightweight and smooth—even on low-end devices",
        "Respects 'reduce motion' accessibility settings"
      ],
    }
  },
  {
    id: "animation-styles",
    title: "Question Animation Styles",
    description: "Choose how questions transition—fade, spring-slide, blur-in, typewriter, morph, flip, reveal, and more.",
    date: "New",
    detailedContent: {
      description: "Give your survey its own personality with motion presets. Pick the transition style between questions and the way options react when selected—from subtle fades to playful springs and typewriter reveals.",
      benefits: [
        "8 transition presets: fade, spring-slide, blur-in, slide-up, typewriter, morph, flip, reveal",
        "Applies to question changes and option selection",
        "Set per survey or let a theme define it",
        "Collapses to a gentle fade for reduced-motion users"
      ],
    }
  },
  {
    id: "interactive-effects",
    title: "Interactive Effects & Sound",
    description: "Magnetic buttons, 3D tilt cards, click ripples, a cursor spotlight, sparkle bursts, and subtle sound.",
    date: "New",
    detailedContent: {
      description: "Delight respondents with tactile micro-interactions. Buttons drift toward the cursor, picture cards tilt in 3D, clicks ripple, a soft spotlight follows the pointer, and selections burst with sparkles—paired with optional subtle sound feedback.",
      benefits: [
        "Magnetic buttons and 3D tilt on cards",
        "Click ripples, cursor spotlight, and sparkle bursts",
        "Optional built-in sound on select, navigate, and complete",
        "Touch-friendly and reduced-motion aware"
      ],
    }
  },
  {
    id: "team-workspaces",
    title: "Team Members & Workspaces",
    description: "Invite teammates with role-based access. Organize surveys into shared workspaces.",
    date: "New",
    detailedContent: {
      description: "Collaborate with your team inside PollBolt. Invite members by email, assign roles (Admin, Editor, Viewer), and organize surveys into separate workspaces for different teams or projects.",
      benefits: [
        "Invite members by email with one click",
        "Role-based access: Owner, Admin, Editor, Viewer",
        "Organize surveys into multiple workspaces",
        "Switch between workspaces from the sidebar"
      ],
    }
  },
  {
    id: "ai-form-builder",
    title: "AI Form Builder Agent",
    description: "Describe what you need—the AI builds a complete, styled survey in seconds. Edit with follow-up prompts.",
    date: "New",
    detailedContent: {
      description: "Our AI agent understands your intent, asks smart follow-ups if needed, then generates a full survey with questions, design, and logic. Keep refining with conversational prompts—it shows its reasoning as it works.",
      benefits: [
        "Describe your survey in plain language or Hebrew",
        "Generates questions, styling, and logic automatically",
        "Iterative: refine with follow-up messages",
        "Visible reasoning steps so you understand its choices",
        "Supports 20+ question types including ranking, matrix, and more"
      ],
    }
  },
  {
    id: "brand-kit",
    title: "Brand Kit",
    description: "Upload your logo and colors once—apply them to every survey with one click.",
    date: "New",
    detailedContent: {
      description: "Set up your brand identity in PollBolt. Upload logos, pick brand colors, and save them as a reusable kit. Apply your brand to any survey instantly, or let the AI generate a matching theme.",
      benefits: [
        "Upload logos and set brand colors",
        "One-click apply to any survey",
        "AI-generated theme from your brand kit",
        "Multiple brand kits for different projects"
      ],
    }
  },
  {
    id: "new-question-types-may",
    title: "6 New Question Types",
    description: "Ranking, Address, Legal/Terms, Video/Audio recording, and more—plus a new Multi-Question Page mode.",
    date: "New",
    detailedContent: {
      description: "A big drop of new question types to make your surveys more powerful. Collect structured addresses, let respondents rank options by priority, gather legal consent with terms acceptance, and record video or audio responses.",
      benefits: [
        "Ranking: Respondents reorder items by priority with up/down controls",
        "Address: Structured fields for street, city, state, zip, and country",
        "Legal / Terms: Scrollable terms text with a required acceptance checkbox",
        "Video / Audio: Record video or audio clips directly in the survey (Pro)",
        "Multi-Question Page: Group multiple questions on a single page"
      ],
    }
  },
  {
    id: "redirect-ending",
    title: "Redirect After Submission",
    description: "Auto-redirect respondents to any URL after they submit, with a configurable countdown.",
    date: "New",
    detailedContent: {
      description: "Add a redirect URL to any ending screen. After submission, respondents see a countdown and are automatically sent to the URL you choose—great for booking pages, follow-up forms, or your website.",
      benefits: [
        "Per-ending-screen redirect URL configuration",
        "Adjustable countdown delay (0–30 seconds)",
        "Fallback 'Click here if not redirected' link",
        "Works alongside existing ending screen content and buttons"
      ],
    }
  },
  {
    id: "slider-signature-types",
    title: "Slider & Signature Questions",
    description: "Drag a range slider for numeric input, or collect a handwritten digital signature.",
    date: "New",
    detailedContent: {
      description: "Two new interactive question types. Slider lets respondents pick a value on a configurable numeric range—great for budgets, satisfaction, or any number input. Signature captures a handwritten signature on a drawing canvas.",
      benefits: [
        "Slider: Configurable min, max, step, and unit label",
        "Slider: Vertical layout on mobile for easier thumb use",
        "Signature: Touch and mouse drawing on an HTML5 canvas",
        "Signature: Saves as an image, visible in analytics and exports"
      ],
    }
  },
  {
    id: "pro-question-types",
    title: "Pro Question Types",
    description: "Matrix, File Upload, Phone Number, and Video/Audio are now available on Pro plans and above.",
    date: "Pro",
    detailedContent: {
      description: "Unlock advanced question types with a Pro subscription. Build Likert-scale grids, accept file uploads, collect validated phone numbers, and let respondents record video or audio.",
      benefits: [
        "Matrix / Likert: Rate multiple items on the same scale in a grid",
        "File Upload: Accept PDFs, images, and documents up to 50 MB",
        "Phone Number: Collect phone numbers with input validation",
        "Video / Audio: Record responses up to 10 minutes (configurable)"
      ],
    }
  },
  {
    id: "public-api-webhooks",
    title: "Public API & Webhooks",
    description: "Manage surveys via REST API and receive real-time webhook callbacks on new responses.",
    date: "New",
    detailedContent: {
      description: "Build automations on top of PollBolt. The Public API lets you create surveys, read responses, and manage your account programmatically. Webhooks deliver instant HTTP callbacks on every new submission.",
      benefits: [
        "Full CRUD access to surveys, responses, and contacts",
        "Real-time POST webhooks on every new response",
        "Generate and revoke API keys from your dashboard",
        "OpenAPI/Swagger documentation included"
      ],
    }
  },
  {
    id: "email-notifications",
    title: "Email Notifications",
    description: "Get instant or daily digest email alerts for new responses. Add multiple recipients.",
    date: "New",
    detailedContent: {
      description: "Enable email notifications per survey. Choose instant alerts or a daily digest, and add teammates or clients as recipients.",
      benefits: [
        "Instant alerts or daily digest mode",
        "Add multiple recipients per survey",
        "Toggle independently for each survey"
      ],
    }
  },
  {
    id: "scheduled-close",
    title: "Scheduled Survey Close",
    description: "Pick a date and time—the survey closes automatically when it arrives.",
    date: "New",
    detailedContent: {
      description: "Set a close date and PollBolt stops accepting responses at the exact moment you choose. Combine with a custom closed message and response limits.",
      benefits: [
        "Set and forget—automatic close on schedule",
        "Custom closed message for late visitors",
        "Combine with response limits for full control"
      ],
    }
  },
  {
    id: "response-limits",
    title: "Response Limits & Auto-Close",
    description: "Cap responses at a number you choose. The survey closes when the limit is reached.",
    date: "New",
    detailedContent: {
      description: "Set a maximum response count. PollBolt closes the survey automatically once the limit is hit—ideal for limited offers, contests, or fixed sample sizes.",
      benefits: [
        "Auto-close at the exact response count",
        "Real-time counter in your dashboard",
        "Increase or remove the limit anytime"
      ],
    }
  },
  {
    id: "partial-responses",
    title: "Partial Response Tracking",
    description: "See who started but didn't finish. Filter by completed vs. partial status.",
    date: "New",
    detailedContent: {
      description: "Responses are auto-saved as respondents progress. Partial submissions appear in your table with a clear status badge so you can analyze drop-off.",
      benefits: [
        "Auto-saved as respondents progress",
        "Filter by completed or partial status",
        "Export both types separately or together"
      ],
    }
  },
  {
    id: "advanced-settings",
    title: "Advanced Survey Settings",
    description: "Password protection, custom closed message, toggle progress bar, and remove branding.",
    date: "Pro",
    detailedContent: {
      description: "Full control over your survey experience—lock access with a password, customize the closed message, toggle the progress bar and question numbers, and remove PollBolt branding.",
      benefits: [
        "Password-protect your survey",
        "Custom closed message",
        "Show/hide progress bar and question numbers",
        "Remove PollBolt branding (paid plans)"
      ],
    }
  },
  {
    id: "redesigned-analytics",
    title: "Redesigned Analytics & Charts",
    description: "Bar, column, pie, donut, and table views with customizable colors and sorting.",
    date: "Improved",
    detailedContent: {
      description: "The analytics page has been rebuilt with five chart types, customizable colors and sorting, and an expandable detail modal. Preferences are saved per survey.",
      benefits: [
        "5 chart types per question",
        "Customizable colors and sort order",
        "Expand any chart into a detail modal",
        "Preferences saved per survey"
      ],
    }
  },
  {
    id: "improved-responses-table",
    title: "Improved Responses Table",
    description: "Search, filter by status or answers, reorder columns, and navigate with keyboard shortcuts.",
    date: "Improved",
    detailedContent: {
      description: "A faster, more powerful responses table. Filter by date, status, or answers. Reorder columns. Open a side panel with arrow-key navigation. Bulk download or delete.",
      benefits: [
        "Filter by status, date, or question answers",
        "Side panel with keyboard navigation",
        "Drag-and-drop column reordering",
        "Bulk select, download, or delete"
      ],
    }
  },
  {
    id: "statement-questions",
    title: "Statement Questions",
    description: "Insert instructions or section headers between questions. No response collected.",
    date: "New",
    detailedContent: {
      description: "Add informational text anywhere in your survey—instructions, section breaks, or context. Statements don't collect a response and are excluded from numbering and analytics.",
      benefits: [
        "Add instructions or section headers",
        "Excluded from numbering and analytics",
        "Full RTL support"
      ],
    }
  },
  {
    id: "ending-screen-buttons",
    title: "Ending Screen Buttons & Question Images",
    description: "Add CTA buttons to ending screens and place images below any question.",
    date: "New",
    detailedContent: {
      description: "Make ending screens actionable with custom buttons linking to any URL. Plus, add images below questions for visual context or branding.",
      benefits: [
        "Custom CTA buttons with text and URL",
        "Multiple buttons per ending screen",
        "Inline images below any question"
      ],
    }
  },
];

export const formatUpdateDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};
